import {
  SiteCoordinateCheck,
  siteCoordinateCheck,
} from '@upright/common/model/input/companies/geolocation/coordinate-check';
import { nameFirstFew, siteLookupLabel } from '@upright/common/model/input/companies/geolocation/geocode-match';
import { SiteRow } from './types';

/**
 * PLT-7875: the words the site table uses to flag a site's coordinates. A neutral badge carrying the
 * word rather than a colour or a glyph (DESIGN.md: every colour has one job, and a mark whose meaning
 * is invented needs the word), with what the gap costs and how to close it behind it.
 */

export const COORDINATE_CHECK_LABEL: Record<SiteCoordinateCheck, string> = {
  missing: 'Missing',
  approximate: 'Approximate',
};

/** The badge's tooltip: what the gap costs the assessment, and how to close it. */
export function coordinateCheckExplanation(row: SiteRow, check: SiteCoordinateCheck): string {
  if (check === 'missing') {
    return 'This site has no coordinates, so it is left out of the physical climate risk assessment. Add a city or street address and identify the coordinates from it, or enter them.';
  }
  if (row.geocodePrecision === 'area') {
    return 'The address matched a whole region or country, so the coordinates mark one point inside it and no hazard value describes the site. Add a city or street address, or enter the coordinates.';
  }
  if (row.geocodePartialMatch === true) {
    return 'Google matched this address only approximately, so the coordinates may be off. Check them, or enter the coordinates yourself.';
  }
  return 'The address matched a place wider than a single site, such as a whole town, so the coordinates mark a point inside it. That is close enough for the hazards mapped on coarse grids, but not for flooding, which is read on a grid of about 1 km. Add a street address, or enter the coordinates.';
}

/**
 * The sentence above the table naming the flagged sites, or null when none is. It names them because
 * the table scrolls sideways and runs long, so a badge can be out of view; it names only the kinds
 * present, so a set with nothing but approximate sites does not mention missing coordinates.
 */
export function describeCoordinateChecks(rows: SiteRow[]): string | null {
  const flagged = rows.flatMap(row => {
    const check = siteCoordinateCheck(row);
    return check === null ? [] : [{ row, check }];
  });
  if (flagged.length === 0) return null;
  const kinds = new Set(flagged.map(({ check }) => check));
  const what =
    kinds.size > 1
      ? 'missing or approximate coordinates'
      : kinds.has('missing')
        ? 'no coordinates'
        : 'approximate coordinates';
  const count = flagged.length === 1 ? '1 site has' : `${flagged.length} sites have`;
  return `${count} ${what}: ${nameFirstFew(flagged.map(({ row }) => siteLookupLabel(row)))}.`;
}
